const CONSENT_STORAGE_KEY = 'gtdrive_consent_v1';
const PURCHASE_STORAGE_PREFIX = 'gtdrive_tracking_purchase_';
const SCRIPT_IDS = {
    gtm: 'gtdrive-google-tag-manager',
    google: 'gtdrive-google-tag',
    meta: 'gtdrive-meta-pixel',
};

let initialized = false;
let pendingEvents = [];
let pageViewQueued = false;

const configuration = () => window.gtdriveTrackingConfig || {};

const normalizeConsent = (value) => ({
    version: 1,
    analytics: value?.analytics === true,
    marketing: value?.marketing === true,
    updatedAt: value?.updatedAt || new Date().toISOString(),
});

const getConsent = () => {
    try {
        const stored = localStorage.getItem(CONSENT_STORAGE_KEY);

        return stored ? normalizeConsent(JSON.parse(stored)) : null;
    } catch (error) {
        return null;
    }
};

const hasConsent = (consent) => consent?.analytics === true || consent?.marketing === true;

const googleTag = (...args) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(args);
};

const updateGoogleConsent = (consent) => {
    googleTag('consent', 'update', {
        analytics_storage: consent.analytics ? 'granted' : 'denied',
        ad_storage: consent.marketing ? 'granted' : 'denied',
        ad_user_data: consent.marketing ? 'granted' : 'denied',
        ad_personalization: consent.marketing ? 'granted' : 'denied',
    });
};

const appendScript = (id, source) => {
    if (document.getElementById(id)) {
        return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = source;
    document.head.appendChild(script);
};

const loadGoogleTagManager = (consent) => {
    const containerId = configuration().gtmContainerId;

    if (! containerId || ! hasConsent(consent) || document.getElementById(SCRIPT_IDS.gtm)) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    updateGoogleConsent(consent);
    window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
    });
    appendScript(SCRIPT_IDS.gtm, `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`);
};

const loadDirectGoogleTags = (consent) => {
    const config = configuration();
    const googleId = consent.analytics && config.ga4MeasurementId
        ? config.ga4MeasurementId
        : (consent.marketing ? config.googleAdsId : null);

    if (! googleId) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || googleTag;
    updateGoogleConsent(consent);
    appendScript(SCRIPT_IDS.google, `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleId)}`);

    if (consent.analytics && config.ga4MeasurementId) {
        googleTag('config', config.ga4MeasurementId, {send_page_view: false});
    }

    if (consent.marketing && config.googleAdsId) {
        googleTag('config', config.googleAdsId);
    }
};

const loadMetaPixel = (consent) => {
    const pixelId = configuration().metaPixelId;

    if (! consent.marketing || ! pixelId || window.fbq) {
        return;
    }

    const fbq = function () {
        fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;

    fbq('consent', 'grant');
    fbq('init', pixelId);
    appendScript(SCRIPT_IDS.meta, 'https://connect.facebook.net/en_US/fbevents.js');
};

const applyConsent = (consent) => {
    const config = configuration();

    updateGoogleConsent(consent);

    if (window.fbq) {
        window.fbq('consent', consent.marketing ? 'grant' : 'revoke');
    }

    if (! config.enabled || ! hasConsent(consent)) {
        return;
    }

    if (config.mode === 'gtm') {
        loadGoogleTagManager(consent);
    } else {
        loadDirectGoogleTags(consent);
        loadMetaPixel(consent);
    }
};

const metaPayload = (payload = {}) => ({
    content_ids: (payload.items || []).map(item => item.item_id),
    contents: (payload.items || []).map(item => ({
        id: item.item_id,
        quantity: item.quantity || 1,
        item_price: item.price || 0,
    })),
    content_name: payload.items?.[0]?.item_name,
    content_type: 'product',
    value: payload.value || 0,
    currency: payload.currency || 'EUR',
});

const alreadyTrackedPurchase = (payload) => {
    if (! payload.transaction_id) {
        return false;
    }

    try {
        return localStorage.getItem(`${PURCHASE_STORAGE_PREFIX}${payload.transaction_id}`) === '1';
    } catch (error) {
        return false;
    }
};

const rememberPurchase = (payload) => {
    if (! payload.transaction_id) {
        return;
    }

    try {
        localStorage.setItem(`${PURCHASE_STORAGE_PREFIX}${payload.transaction_id}`, '1');
    } catch (error) {
        // Le suivi ne doit jamais bloquer le parcours d'achat.
    }
};

const send = (event, payload = {}) => {
    const config = configuration();
    const consent = getConsent();

    if (! config.enabled || ! consent || ! hasConsent(consent)) {
        return false;
    }

    if (event === 'purchase' && alreadyTrackedPurchase(payload)) {
        return false;
    }

    let sent = false;

    if (config.mode === 'gtm' && config.gtmContainerId) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({event, ecommerce: payload});
        sent = true;
    }

    if (config.mode === 'direct') {
        if (consent.analytics && config.ga4MeasurementId) {
            googleTag('event', event, payload);
            sent = true;
        }

        if (
            event === 'purchase'
            && consent.marketing
            && config.googleAdsId
            && config.googleAdsPurchaseLabel
        ) {
            googleTag('event', 'conversion', {
                send_to: `${config.googleAdsId}/${config.googleAdsPurchaseLabel}`,
                value: payload.value,
                currency: payload.currency || 'EUR',
                transaction_id: payload.transaction_id,
            });
            sent = true;
        }

        if (consent.marketing && config.metaPixelId && window.fbq) {
            const metaEvents = {
                page_view: 'PageView',
                view_item: 'ViewContent',
                add_to_cart: 'AddToCart',
                purchase: 'Purchase',
            };

            if (metaEvents[event]) {
                window.fbq('track', metaEvents[event], event === 'page_view' ? {} : metaPayload(payload));
                sent = true;
            }
        }
    }

    if (event === 'purchase' && sent) {
        rememberPurchase(payload);
    }

    return sent;
};

const flush = () => {
    const events = pendingEvents;
    pendingEvents = [];
    events.forEach(({event, payload}) => send(event, payload));
};

const track = (event, payload = {}) => {
    if (! getConsent()) {
        pendingEvents.push({event, payload});

        return;
    }

    send(event, payload);
};

const saveConsent = (value) => {
    const previousConsent = getConsent();
    const hasQueuedPageView = pendingEvents.some(({event}) => event === 'page_view');
    const consent = normalizeConsent(value);

    try {
        localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    } catch (error) {
        // En navigation privée restrictive, le choix reste valable pour la page.
    }

    applyConsent(consent);

    if (hasConsent(consent)) {
        flush();

        if (! hasConsent(previousConsent) && ! hasQueuedPageView) {
            send('page_view', {
                page_location: window.location.href,
                page_title: document.title,
            });
        }
    } else {
        pendingEvents = [];
    }

    window.dispatchEvent(new CustomEvent('gtdrive:consent-changed', {detail: consent}));

    return consent;
};

const init = () => {
    if (initialized || ! configuration().enabled) {
        return;
    }

    initialized = true;

    const consent = getConsent();
    if (consent) {
        applyConsent(consent);
    } else {
        updateGoogleConsent(normalizeConsent({analytics: false, marketing: false}));
    }

    const queued = window.gtdriveTrackingQueue || [];
    window.gtdriveTrackingQueue = [];
    queued.forEach(({event, payload}) => track(event, payload));

    if (! pageViewQueued) {
        pageViewQueued = true;
        track('page_view', {
            page_location: window.location.href,
            page_title: document.title,
        });
    }
};

const installCartTracking = (axios) => {
    axios.interceptors.response.use(response => {
        const method = response.config?.method?.toLowerCase();
        const url = response.config?.url?.split('?')[0];

        if (method === 'post' && url?.endsWith('/api/checkout/cart') && response.data?.tracking) {
            track('add_to_cart', response.data.tracking);
        }

        return response;
    });
};

const openConsent = () => window.dispatchEvent(new CustomEvent('gtdrive:consent-open'));

export default {
    getConsent,
    init,
    installCartTracking,
    openConsent,
    saveConsent,
    track,
};
